import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  AfdrachtsVermindering, BelastNormaalTarief, BelastVerdragsTarief,
  BokkzAlgDataService, BokkzDivDataService, BokkzDivLogic,
  Dividendbelasting,
  DividendTaxEntity, Fonds, Gerechtigde, InhoudingsVrijstelling,
  LiquidatieUitkering,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {Subscription} from "rxjs";
import {SharedUiAlertModule} from "@snelstart/ui-alert";

@Component({
  selector: 'snelstart-venture-fiscaal-inhoudingsvrijstelling',
  standalone: true,
    imports: [CommonModule, SharedUiDetailsModule, FormsModule, ReactiveFormsModule, SharedUiButtonModule, SharedUiFormControlsModule, SharedUiAlertModule],
  templateUrl: './inhoudingsvrijstelling.component.html',
  styleUrl: './inhoudingsvrijstelling.component.scss',
})
export class InhoudingsvrijstellingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private divTaxEntitySubscription: Subscription | undefined;
  public divTaxEntity = {} as DividendTaxEntity;
  public aangifte = {} as Dividendbelasting;
  public dividendFg = {} as FormGroup;
  public inhoudingsVrijstellingFg = {} as FormGroup;
  public index = 0;
  public formStructure: any;
  public focusedElementId: string | null = null;
  public totaalPercentageBelang = 0;

  constructor(
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzDivDataService,
    public divService: BokkzDivLogic,
    private fb: FormBuilder) {
    this.dividendFg = this.fb.group({
      subfund: null as unknown as Fonds,
      bedragDividenduitkering: null as unknown as number,
      bedragOnbelastVerbodExtraterritorialeHeffing: null as unknown as number,
      liquidatieUitkering: null as unknown as LiquidatieUitkering,
      inhoudingsVrijstelling: null as unknown as InhoudingsVrijstelling,
      belastVerdragsTarief: null as unknown as BelastVerdragsTarief,
      belastNormaalTarief: null as unknown as BelastNormaalTarief,
      afdrachtsVermindering: null as unknown as AfdrachtsVermindering,
    })
    this.inhoudingsVrijstellingFg = this.fb.group({
      opbrengstGerechtigden: this.fb.array<Gerechtigde>([]),
    })
  }

  get opbrengstGerechtigden(): FormArray {
    return this.inhoudingsVrijstellingFg.get('opbrengstGerechtigden') as FormArray;
  }

  addRij(): void {
    this.opbrengstGerechtigden.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.opbrengstGerechtigden.removeAt(index);
    this.saveForm()
  }

  createFormGroup(item?: Gerechtigde): FormGroup {
    return this.fb.group<Gerechtigde>({
      fiscaalNummerGerechtigde: item ? item.fiscaalNummerGerechtigde : null as unknown as string,
      naamGerechtigde: item ? item.naamGerechtigde : null as unknown as string,
      dividendGerechtigde: item ? item.dividendGerechtigde : null as unknown as number,
      percentageBelang: item ? item.percentageBelang : null as unknown as number,
      indicatieKwalificatieBeschikking: item ? item.indicatieKwalificatieBeschikking : false,
      aantalAandelen: item ? item.aantalAandelen : null as unknown as number,
      dividendPerAandeel: item ? item.dividendPerAandeel : null as unknown as number,
    });
  }

  trackByFn(index: number): any {
    return index;
  }

  ngOnInit(): void {
    this.divTaxEntitySubscription = this.dataService.dividendTaxEntity.subscribe((value) => {
      this.divTaxEntity = value;
      this.aangifte = value.aangifte!;
      if (this.aangifte.aangifteDb?.dividendSpecificaties?.at(0)) {
        this.dividendFg.patchValue(this.aangifte.aangifteDb?.dividendSpecificaties[0], {emitEvent: false})
      }
      if (this.aangifte.aangifteDb?.dividendSpecificaties?.at(0)?.inhoudingsVrijstelling!) {
        this.inhoudingsVrijstellingFg.patchValue(this.aangifte.aangifteDb?.dividendSpecificaties?.at(0)?.inhoudingsVrijstelling!, {emitEvent: false})
        this.updateForm();
      }
      const dividendSpecificaties = this.aangifte?.aangifteDb?.dividendSpecificaties ?? [];
      const totaalPercentageBelangNormaalTarief = dividendSpecificaties
          .flatMap(spec => spec.belastNormaalTarief?.aanmerkelijkBelangHouders ?? [])
          .map(houder => houder.percentageBelang ?? 0)
          .reduce((totaal, perc) => totaal + perc, 0);
      const totaalPercentageBelangVrijstelling = dividendSpecificaties
          .flatMap(spec => spec.inhoudingsVrijstelling?.opbrengstGerechtigden ?? [])
          .map(houder => houder.percentageBelang ?? 0)
          .reduce((totaal, perc) => totaal + perc, 0);
      this.totaalPercentageBelang = totaalPercentageBelangNormaalTarief + totaalPercentageBelangVrijstelling;
    })
  }

  private updateForm(): void {
    const lijst = this.aangifte.aangifteDb?.dividendSpecificaties?.at(0)?.inhoudingsVrijstelling?.opbrengstGerechtigden || [];
    const formArray = this.inhoudingsVrijstellingFg.get('opbrengstGerechtigden') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i]);
      } else {
        const formGroup = this.createFormGroup(lijst[i])
        formArray.push(formGroup);
      }
    }
    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.divTaxEntitySubscription) {
      this.divTaxEntitySubscription.unsubscribe()
    }
  }

  saveForm(): void {
    this.dividendFg.updateValueAndValidity()
    this.inhoudingsVrijstellingFg.updateValueAndValidity()
    this.dividendFg.value.inhoudingsVrijstelling = this.inhoudingsVrijstellingFg.value;
    this.divTaxEntity.aangifte = {
      ...this.aangifte,
      aangifteDb: {
        ...this.aangifte.aangifteDb,
        dividendSpecificaties: [this.dividendFg.value],
      }
    } as Dividendbelasting;
    this.dataService.updateDividendTaxEntity(this.divTaxEntity);
    this.divService.getCalculation(this.divTaxEntity);
  }
}

